import * as React from "react";
import { format } from "date-fns";
import { X } from "lucide-react";

import { cn } from "@/shared/lib/utils";

import { EntryForm } from "./EntryForm";
import type { TimeEntryFormValues } from "./schema";
import type { Abbreviation } from "@/shared/components/NarrativeTextArea/NarrativeTextArea";

// ── Types (matching old AddEntryDialog interface) ──────────────────

type Matter = {
  matter: string;
  matter_id: string;
  client: string;
  description: string;
  task_group: string;
  activity_group: string;
};

type Task = {
  task: string;
  task_description: string;
  task_group: string;
};

type Activity = {
  activity: string;
  activity_description: string;
  activity_group: string;
};

type AddEntryV2Props = {
  onSave: (
    newEntry: {
      id: number;
      date: string;
      matter: string;
      matter_id: string;
      description: string;
      duration: number;
      task: string;
      activity: string;
    },
    shouldExport: boolean,
  ) => void;
  matters: Matter[];
  tasks: Task[];
  activities: Activity[];
  currentDate: Date;
  hardcodedMatter?: string;
  lastUsedMatter?: string;
  abbreviations?: Abbreviation[];
};

export type AddEntryV2Handle = {
  openWithDuration: (units: number) => void;
};

// ── Component ──────────────────────────────────────────────────────

const AddEntryV2 = React.forwardRef<AddEntryV2Handle, AddEntryV2Props>(
  ({ onSave, matters, tasks, activities, currentDate, hardcodedMatter, lastUsedMatter, abbreviations }, ref) => {
    const [open, setOpen] = React.useState(false);
    const [initialDuration, setInitialDuration] = React.useState(0);
    const panelRef = React.useRef<HTMLDivElement>(null);

    React.useImperativeHandle(
      ref,
      () => ({
        openWithDuration: (units: number) => {
          setInitialDuration(units);
          setOpen(true);
        },
      }),
      [],
    );

    // Close on Escape (only if no child dropdown is open)
    React.useEffect(() => {
      if (!open) return;
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === "Escape") {
          // Don't close if a combobox dropdown or popover is open inside
          const openDropdowns = panelRef.current?.querySelectorAll(
            '[role="listbox"], [data-state="open"]',
          );
          if (openDropdowns && openDropdowns.length > 0) return;
          setOpen(false);
        }
      };
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }, [open]);

    const toEntry = (values: TimeEntryFormValues) => ({
      id: 0,
      date: values.date ? format(values.date, "MM/dd/yyyy") : "",
      matter: values.matter,
      matter_id: values.matter_id,
      description: values.description,
      duration: values.duration,
      task: values.task,
      activity: values.activity,
    });

    const handleSave = React.useCallback(
      (values: TimeEntryFormValues) => {
        onSave(toEntry(values), false);
        setOpen(false);
        setInitialDuration(0);
      },
      [onSave],
    );

    const handleExport = React.useCallback(
      (values: TimeEntryFormValues) => {
        onSave(toEntry(values), true);
        setOpen(false);
        setInitialDuration(0);
      },
      [onSave],
    );

    const handleDuplicate = React.useCallback(
      (values: TimeEntryFormValues) => {
        onSave(toEntry(values), false);
        // Keep dialog open for re-entry
      },
      [onSave],
    );

    if (!open) return null;

    return (
      <div className="fixed inset-0 z-50">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/80"
          onClick={() => setOpen(false)}
        />

        {/* Panel */}
        <div
          ref={panelRef}
          className={cn(
            "fixed left-1/2 top-1/2 z-50 w-full max-w-xl -translate-x-1/2 -translate-y-1/2",
            "rounded-lg border bg-card p-6 shadow-lg",
            "border-border text-foreground",
          )}
        >
          {/* Close button */}
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="absolute right-4 top-4 rounded-sm opacity-70 hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </button>

          {/* Header */}
          <div className="mb-4">
            <h2 className="text-lg font-sans font-semibold leading-none tracking-tight">
              Add entry
            </h2>
            <p className="mt-1.5 text-sm text-muted-foreground">
              Manually submit an entry here.
            </p>
          </div>

          <EntryForm
            matters={matters}
            tasks={tasks}
            activities={activities}
            currentDate={currentDate}
            hardcodedMatter={hardcodedMatter}
            lastUsedMatter={lastUsedMatter}
            defaultValues={{
              duration: initialDuration,
              date: currentDate || new Date(),
            }}
            onSubmit={handleSave}
            onExport={handleExport}
            onDuplicate={handleDuplicate}
            submitLabel="Add entry"
            abbreviations={abbreviations}
          />
        </div>
      </div>
    );
  },
);

AddEntryV2.displayName = "AddEntryV2";

export default AddEntryV2;
